import { EditOutlined, EyeOutlined, InfoCircleFilled, StopOutlined } from '@ant-design/icons';
import { AnimatePresence, motion } from 'motion/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ApiError, api, newIdempotencyKey, type PageInfo } from '../api/client';
import type { components } from '../api/schema.gen';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { EmptyState } from '../components/EmptyState';
import { Modal } from '../components/Modal';
import { OnboardingNotice } from '../components/OnboardingNotice';
import { PageHeading } from '../components/PageHeading';
import { PageLoader } from '../components/PageLoader';
import { CursorPagePager } from '../components/Pagination';
import { SelectMenu } from '../components/SelectMenu';
import { formatDate, formatDateTime } from '../lib/dateFormat';
import { gatewayOpenAiBaseUrl } from '../lib/gatewayUrl';
import { useLanguage } from '../lib/i18n/LanguageContext';
import { fadeUp } from '../lib/motionPresets';
import { useSession } from '../lib/SessionContext';
import { useCopyToClipboard } from '../lib/useCopyToClipboard';

type ApiKey = components['schemas']['ApiKey'];
type CreatedKey = ApiKey & { secret: string };

const PAGE_SIZE_OPTIONS = [10, 20, 50] as const;
const DEFAULT_PAGE_SIZE = 20;

/** Preset durations for the create form's expiration picker — "1 hour" has
 * to mean exactly one hour, not "expires today", so this is duration-from-
 * submit-time math (see expiresAtFromPreset), not a date picker. `none`
 * means never expires. */
const CREATE_EXPIRY_PRESETS = ['none', '1h', '1d', '7d', '30d', '90d', '180d', '1y'] as const;
type CreateExpiryPreset = (typeof CREATE_EXPIRY_PRESETS)[number];

/** The edit modal adds one more option ahead of the create-form set: keeping
 * whatever the key's expiry currently is untouched. That is NOT the same as
 * `none` (which actively clears an existing expiry back to "never") — it is
 * the "omit this field from the PATCH body" case api.updateKey's partial
 * update relies on, so it needs its own value, not a repurposed one. */
const EDIT_EXPIRY_PRESETS = ['keep', ...CREATE_EXPIRY_PRESETS] as const;
type EditExpiryPreset = (typeof EDIT_EXPIRY_PRESETS)[number];

const EXPIRY_PRESET_HOURS: Record<Exclude<CreateExpiryPreset, 'none'>, number> = {
  '1h': 1,
  '1d': 24,
  '7d': 24 * 7,
  '30d': 24 * 30,
  '90d': 24 * 90,
  '180d': 24 * 180,
  '1y': 24 * 365,
};

/** A preset -> the instant it resolves to AT THE TIME THIS IS CALLED, i.e.
 * submit time, not selection time — a form left open for a while must still
 * expire "1 hour" from when it was actually submitted. undefined means
 * "omit expires_at from the request body" (create: defaults to never
 * expires; edit: leaves the existing value alone) — see api.createKey and
 * api.updateKey for why omitted and explicit-null are not interchangeable. */
function expiresAtFromPreset(preset: CreateExpiryPreset | EditExpiryPreset): string | null | undefined {
  if (preset === 'keep') return undefined;
  if (preset === 'none') return null;
  const hours = EXPIRY_PRESET_HOURS[preset];
  return new Date(Date.now() + hours * 60 * 60 * 1000).toISOString();
}

const SORT_OPTIONS = ['created_desc', 'expires_asc', 'expires_desc'] as const;
type SortOption = (typeof SORT_OPTIONS)[number];

/** Sorts only the page(s) already loaded into `keys` — this list is
 * cursor-paginated, not re-fetched sorted from the server, so "sort" here
 * means "reorder what's on screen", same as any other client-side table
 * sort over a partially-loaded set. Keys with no expiry sort after every
 * key that has one, in both expiry sort directions, since "never" isn't a
 * point on the timeline the other dates can be compared against. */
function sortKeys(keys: ApiKey[], sortBy: SortOption): ApiKey[] {
  const sorted = [...keys];
  if (sortBy === 'created_desc') {
    sorted.sort((a, b) => b.created_at.localeCompare(a.created_at));
  } else {
    sorted.sort((a, b) => {
      if (!a.expires_at && !b.expires_at) return 0;
      if (!a.expires_at) return 1;
      if (!b.expires_at) return -1;
      return sortBy === 'expires_asc'
        ? a.expires_at.localeCompare(b.expires_at)
        : b.expires_at.localeCompare(a.expires_at);
    });
  }
  return sorted;
}

export function Keys() {
  const { ready, hasOrg } = useSession();
  const { dict } = useLanguage();
  const [keys, setKeys] = useState<ApiKey[] | null>(null);
  const [page, setPage] = useState<PageInfo | null>(null);
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [paging, setPaging] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [onboarding, setOnboarding] = useState(false);
  const cursors = useRef<(string | undefined)[]>([undefined]);

  const [name, setName] = useState('');
  const [note, setNote] = useState('');
  // 'none' by default: most keys should not expire, and defaulting to any
  // finite preset would be an actively dangerous default to ship.
  const [expiryPreset, setExpiryPreset] = useState<CreateExpiryPreset>('none');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createIdempotencyKey, setCreateIdempotencyKey] = useState(() => newIdempotencyKey());
  const [newKey, setNewKey] = useState<CreatedKey | null>(null);

  const [detailTarget, setDetailTarget] = useState<ApiKey | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<ApiKey | null>(null);
  const [revokeBusy, setRevokeBusy] = useState(false);
  const [revokeError, setRevokeError] = useState<string | null>(null);

  const [editTarget, setEditTarget] = useState<ApiKey | null>(null);
  const [sortBy, setSortBy] = useState<SortOption>('created_desc');
  const sortedKeys = useMemo(() => (keys ? sortKeys(keys, sortBy) : null), [keys, sortBy]);

  /**
   * Fetch one page of keys. Keyset walk keeps a cursor trail so Previous can
   * replay its way back — same shape Reports / TopUp use for cursor lists.
   * A mutation (create/revoke) reloads page 1 because it changes what that
   * page contains.
   */
  const loadPage = useCallback(
    async (wanted: number, size = pageSize) => {
      setPaging(true);
      try {
        const res = await api.keys({
          limit: size,
          cursor: cursors.current[wanted - 1],
        });
        setKeys(res.data);
        setPage(res.page);
        setPageIndex(wanted);
        cursors.current[wanted] = res.page.next_cursor ?? undefined;
        setListError(null);
      } catch (e) {
        if (e instanceof ApiError && e.status === 403) setOnboarding(true);
        else setListError(e instanceof ApiError ? (e.problem.detail ?? e.problem.title) : dict.keys.listError);
      } finally {
        setPaging(false);
      }
    },
    [pageSize, dict],
  );

  const load = useCallback(async () => {
    cursors.current = [undefined];
    await loadPage(1);
  }, [loadPage]);

  useEffect(() => {
    if (!ready) return;
    if (!hasOrg) {
      setOnboarding(true);
      return;
    }
    void load();
  }, [ready, hasOrg, load]);

  // The secret is shown exactly once. Losing the tab — a refresh, an
  // accidental back-navigation — loses it for good, so this is one honest
  // line of defence while it is still on screen.
  useEffect(() => {
    if (!newKey) return;
    function onBeforeUnload(e: BeforeUnloadEvent) {
      e.preventDefault();
      e.returnValue = '';
    }
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [newKey]);

  async function createKey(e: React.FormEvent) {
    e.preventDefault();
    setCreating(true);
    setCreateError(null);
    try {
      // The idempotency key is generated once, when the customer opens this
      // form (see state initialiser), and reused across retries of the same
      // attempt. A fresh one per keystroke or per submit would defeat it.
      // expiresAtFromPreset is computed HERE, at actual submit time, not
      // when the preset was picked — "1 hour" has to mean one hour from
      // when the key is really created, not from whenever the dropdown was
      // touched. `?? undefined` folds the preset function's `null` (which
      // only means something for the edit modal's partial-update body) into
      // createKey's own "never expires" default.
      const created = await api.createKey(
        name.trim(),
        createIdempotencyKey,
        expiresAtFromPreset(expiryPreset) ?? undefined,
        note.trim() || undefined,
      );
      setNewKey(created);
      setName('');
      setNote('');
      setExpiryPreset('none');
      setCreateIdempotencyKey(newIdempotencyKey());
      await load();
    } catch (e) {
      setCreateError(e instanceof ApiError ? (e.problem.detail ?? e.problem.title) : dict.keys.createError);
    } finally {
      setCreating(false);
    }
  }

  async function confirmRevoke() {
    if (!revokeTarget) return;
    setRevokeBusy(true);
    setRevokeError(null);
    try {
      await api.revokeKey(revokeTarget.key_id);
      setRevokeTarget(null);
      await load();
    } catch (e) {
      setRevokeError(e instanceof ApiError ? (e.problem.detail ?? e.problem.title) : dict.keys.revokeError);
    } finally {
      setRevokeBusy(false);
    }
  }

  return (
    <div className="page keys-page">
      <PageHeading subtitle={dict.keys.subtitle}>{dict.common.apiKeys}</PageHeading>

      {onboarding ? (
        <OnboardingNotice area={dict.onboarding.keysArea} />
      ) : (
        <>
          <motion.section variants={fadeUp} initial="hidden" animate="show" className="panel keys-panel keys-panel--create">
            <div className="panel-heading-row">
              <h2>{dict.keys.createHeading}</h2>
              <a href="/guide#keys" className="guide-link-btn">
                {dict.keys.viewSetupGuide}
              </a>
            </div>
            <form onSubmit={createKey} className="keys-create-form">
              <div className="inline-form-field">
                <label htmlFor="key-name">{dict.keys.nameLabel}</label>
                <input
                  id="key-name"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    // Editing the name changes the request body, so the
                    // idempotency key must change with it — reusing it would
                    // hit a 409 (same key, different body) rather than the
                    // retry-safety it exists for. It stays fixed across
                    // repeated submits of an *unchanged* name, which is the
                    // actual retry case this protects.
                    setCreateIdempotencyKey(newIdempotencyKey());
                  }}
                  placeholder={dict.keys.namePlaceholder}
                  maxLength={120}
                  required
                />
              </div>
              <div className="inline-form-field">
                <label htmlFor="key-note">{dict.keys.noteLabel}</label>
                <input
                  id="key-note"
                  value={note}
                  onChange={(e) => {
                    setNote(e.target.value);
                    // Same reasoning as the name field: note is part of the
                    // request body, so a change must mint a fresh idempotency key.
                    setCreateIdempotencyKey(newIdempotencyKey());
                  }}
                  placeholder={dict.keys.notePlaceholder}
                  maxLength={500}
                />
              </div>
              <div className="keys-create-options">
                <div className="inline-form-field keys-create-expires">
                  <label htmlFor="key-expires">{dict.keys.expiresLabel}</label>
                  <SelectMenu
                    id="key-expires"
                    className="expiry-select"
                    aria-label={dict.keys.expiresLabel}
                    value={expiryPreset}
                    onChange={(v) => {
                      setExpiryPreset(v);
                      // Part of the request body, same reasoning as name above.
                      setCreateIdempotencyKey(newIdempotencyKey());
                    }}
                    options={CREATE_EXPIRY_PRESETS.map((p) => ({ value: p, label: dict.keys.expiryPresetLabel[p] }))}
                  />
                </div>
                <button type="submit" className="keys-create-submit" disabled={creating || name.trim() === ''}>
                  {creating ? dict.keys.creating : dict.keys.createButton}
                </button>
              </div>
            </form>
            {createError && <div className="err">{createError}</div>}
          </motion.section>

          <motion.section variants={fadeUp} initial="hidden" animate="show" className="panel keys-panel keys-panel--list">
            <div className="panel-heading-row">
              <h2>{dict.keys.yourKeys}</h2>
              <div className="sort-control">
                <label htmlFor="keys-sort">{dict.keys.sortLabel}</label>
                <SelectMenu
                  id="keys-sort"
                  className="sort-select"
                  aria-label={dict.keys.sortLabel}
                  value={sortBy}
                  onChange={setSortBy}
                  options={SORT_OPTIONS.map((o) => ({ value: o, label: dict.keys.sortOptions[o] }))}
                />
              </div>
            </div>
            {listError && <div className="err">{listError}</div>}

            {keys === null ? (
              <PageLoader />
            ) : (
            <div className="table-scroll">
              <table role="table" className="keys-table">
                <thead role="rowgroup">
                  <tr role="row">
                    <th role="columnheader">{dict.keys.colName}</th>
                    <th role="columnheader">{dict.keys.colPrefix}</th>
                    <th role="columnheader">{dict.keys.colStatus}</th>
                    <th role="columnheader">{dict.keys.colCreated}</th>
                    <th role="columnheader">{dict.keys.colExpires}</th>
                    <th role="columnheader">{dict.keys.colLastUsed}</th>
                    <th role="columnheader" aria-label={dict.keys.actionsAria} />
                  </tr>
                </thead>
                <tbody role="rowgroup">
                  {keys.length === 0 && (
                    <tr role="row">
                      <td role="cell" colSpan={7}>
                        <EmptyState title={dict.keys.emptyTitle}>
                          <p className="note">{dict.keys.emptyBody}</p>
                        </EmptyState>
                      </td>
                    </tr>
                  )}
                  {sortedKeys?.map((k) => (
                    <tr role="row" key={k.key_id}>
                      <td role="cell" data-label={dict.keys.colName}>
                        <span className="keys-name">{k.name}</span>
                      </td>
                      <td role="cell" data-label={dict.keys.colPrefix}>
                        <code className="keys-prefix">{k.prefix}</code>
                      </td>
                      <td role="cell" data-label={dict.keys.colStatus}>
                        <span className={`badge badge--${k.status}`}>{dict.keys.status[k.status]}</span>
                      </td>
                      <td role="cell" data-label={dict.keys.colCreated}>
                        {formatDate(k.created_at)}
                      </td>
                      <td role="cell" data-label={dict.keys.colExpires}>
                        {k.expires_at ? (
                          formatDateTime(k.expires_at)
                        ) : (
                          <span className="muted">{dict.keys.neverExpires}</span>
                        )}
                      </td>
                      <td role="cell" data-label={dict.keys.colLastUsed}>
                        {k.last_used_at ? (
                          formatDateTime(k.last_used_at)
                        ) : (
                          <span className="muted">{dict.keys.neverUsed}</span>
                        )}
                      </td>
                      <td role="cell" className="num" data-label={dict.keys.actionsAria}>
                        <div className="row-actions">
                          <button
                            type="button"
                            className="ghost icon-btn"
                            data-tooltip={dict.keys.detail}
                            aria-label={dict.keys.detail}
                            onClick={() => setDetailTarget(k)}
                          >
                            <EyeOutlined aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            className="ghost icon-btn"
                            data-tooltip={dict.keys.edit}
                            aria-label={dict.keys.edit}
                            disabled={k.status !== 'active'}
                            onClick={() => setEditTarget(k)}
                          >
                            <EditOutlined aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            className="ghost danger-text icon-btn"
                            data-tooltip={dict.keys.revoke}
                            aria-label={dict.keys.revoke}
                            disabled={k.status !== 'active'}
                            onClick={() => setRevokeTarget(k)}
                          >
                            <StopOutlined aria-hidden="true" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}
            <CursorPagePager
              page={page}
              loaded={keys?.length ?? 0}
              pageIndex={pageIndex}
              busy={paging}
              onPrev={() => void loadPage(pageIndex - 1)}
              onNext={() => void loadPage(pageIndex + 1)}
              pageSize={pageSize}
              pageSizeOptions={[...PAGE_SIZE_OPTIONS]}
              onPageSizeChange={(size) => {
                // load() re-runs via the ready/hasOrg effect once pageSize
                // updates loadPage's identity — start the cursor walk over.
                setPageSize(size);
                cursors.current = [undefined];
              }}
            />
          </motion.section>
        </>
      )}

      <AnimatePresence>
        {newKey && <SecretReveal apiKey={newKey} onDismiss={() => setNewKey(null)} />}
      </AnimatePresence>

      <AnimatePresence>
        {detailTarget && <KeyDetail apiKey={detailTarget} onClose={() => setDetailTarget(null)} />}
      </AnimatePresence>

      <AnimatePresence>
        {editTarget && (
          <KeyEditRateLimit
            apiKey={editTarget}
            onClose={() => setEditTarget(null)}
            onSaved={async () => {
              setEditTarget(null);
              await load();
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {revokeTarget && (
          <ConfirmDialog
            title={dict.keys.revokeTitle}
            body={
              <>
                <p>
                  <strong>{revokeTarget.name}</strong>
                  {dict.keys.revokeBodySuffix(revokeTarget.prefix)}
                </p>
                <p className="note">{dict.keys.revokeBodyWarning}</p>
              </>
            }
            confirmLabel={dict.keys.revokeConfirmLabel}
            danger
            busy={revokeBusy}
            error={revokeError}
            onConfirm={confirmRevoke}
            onCancel={() => {
              setRevokeTarget(null);
              setRevokeError(null);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

/**
 * Key metadata for keys that already exist — the full secret is never here.
 * Prefix is copyable so customers can match a key against logs or configs
 * without needing the one-time secret reveal again.
 */
function KeyDetail({ apiKey, onClose }: { apiKey: ApiKey; onClose: () => void }) {
  const { dict } = useLanguage();
  const { copied, copy } = useCopyToClipboard();

  return (
    <Modal title={dict.keys.detailTitle(apiKey.name)} onClose={onClose}>
      <p className="note">{dict.keys.detailPrefixNote}</p>

      <div className="secret-box">
        <code className="keys-prefix keys-prefix--plain">{apiKey.prefix}</code>
        <button type="button" className="ghost" onClick={() => void copy(apiKey.prefix)}>
          {copied ? dict.keys.copied : dict.keys.copy}
        </button>
      </div>

      <dl className="detail-grid">
        <div>
          <dt>{dict.keys.colRouting}</dt>
          <dd>{dict.keys.routing[apiKey.routing_policy]}</dd>
        </div>
        <div>
          <dt>{dict.keys.colStatus}</dt>
          <dd>
            <span className={`badge badge--${apiKey.status}`}>{dict.keys.status[apiKey.status]}</span>
          </dd>
        </div>
        <div>
          <dt>{dict.keys.colCreated}</dt>
          <dd>{formatDateTime(apiKey.created_at)}</dd>
        </div>
        <div>
          <dt>{dict.keys.colLastUsed}</dt>
          <dd>
            {apiKey.last_used_at ? formatDateTime(apiKey.last_used_at) : dict.keys.neverUsed}
          </dd>
        </div>
        <div>
          <dt>{dict.keys.colRateLimit}</dt>
          <dd>
            {apiKey.rate_limit_rpm != null
              ? dict.keys.rateLimitPerMinute(apiKey.rate_limit_rpm)
              : dict.keys.rateLimitUnlimited}
          </dd>
        </div>
        <div>
          <dt>{dict.keys.expiresLabel}</dt>
          <dd>{apiKey.expires_at ? formatDateTime(apiKey.expires_at) : dict.keys.neverExpires}</dd>
        </div>
        <div>
          <dt>{dict.keys.colNote}</dt>
          <dd>{apiKey.note ? apiKey.note : '—'}</dd>
        </div>
      </dl>

      <div className="modal-actions">
        <button type="button" className="ghost" onClick={onClose}>
          {dict.common.close}
        </button>
      </div>
    </Modal>
  );
}

/**
 * Edits the two mutable fields a key has today: its per-key
 * requests-per-minute override (LLMR-22) and its expiration date (LLMR-51).
 * Both use the same "unlimited/never checkbox + disabled input" shape, and
 * both submit `null` for "no restriction" — a real absence sent to the
 * server, not a client-side default standing in for one.
 */
function KeyEditRateLimit({
  apiKey,
  onClose,
  onSaved,
}: {
  apiKey: ApiKey;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const { dict } = useLanguage();
  const [unlimited, setUnlimited] = useState(apiKey.rate_limit_rpm == null);
  const [value, setValue] = useState(apiKey.rate_limit_rpm != null ? String(apiKey.rate_limit_rpm) : '');
  // 'keep' does not correspond to any single instant on the existing key
  // (there is no clean reverse-mapping from an arbitrary expires_at back to
  // one of these presets), so it is its own value rather than trying to
  // pre-select the "closest" duration — see expiresAtFromPreset's 'keep'
  // case, which is what actually keeps the PATCH body's expires_at omitted.
  const [expiryPreset, setExpiryPreset] = useState<EditExpiryPreset>('keep');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const parsed = Number.parseInt(value, 10);
  const rateLimitValid = unlimited || (value.trim() !== '' && Number.isFinite(parsed) && parsed > 0);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!rateLimitValid) return;
    setBusy(true);
    setError(null);
    try {
      await api.updateKey(apiKey.key_id, {
        rateLimitRPM: unlimited ? null : parsed,
        expiresAt: expiresAtFromPreset(expiryPreset),
      });
      await onSaved();
    } catch (e) {
      setError(e instanceof ApiError ? (e.problem.detail ?? e.problem.title) : dict.keys.editError);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={dict.keys.editTitle(apiKey.name)} onClose={onClose}>
      <form onSubmit={save}>
        <div className="inline-form-field">
          <label htmlFor="edit-rate-limit">{dict.keys.rateLimitLabel}</label>
          <input
            id="edit-rate-limit"
            type="number"
            min={1}
            step={1}
            value={value}
            disabled={unlimited}
            onChange={(e) => setValue(e.target.value)}
            placeholder={dict.keys.rateLimitPlaceholder}
          />
        </div>
        <label className="checkbox-row">
          <input
            type="checkbox"
            checked={unlimited}
            onChange={(e) => setUnlimited(e.target.checked)}
          />
          {dict.keys.rateLimitUnlimitedLabel}
        </label>
        <p className="note">{dict.keys.rateLimitHelp}</p>

        <div className="inline-form-field">
          <label htmlFor="edit-expires">{dict.keys.expiresLabel}</label>
          <SelectMenu
            id="edit-expires"
            className="expiry-select"
            aria-label={dict.keys.expiresLabel}
            value={expiryPreset}
            onChange={setExpiryPreset}
            options={EDIT_EXPIRY_PRESETS.map((p) => ({
              value: p,
              label: p === 'keep' ? dict.keys.expiryKeepCurrent : dict.keys.expiryPresetLabel[p],
            }))}
          />
          <p className="note">
            {apiKey.expires_at
              ? dict.keys.expiryCurrentlyExpires(formatDateTime(apiKey.expires_at))
              : dict.keys.expiryCurrentlyNever}
          </p>
        </div>

        {error && <div className="err">{error}</div>}

        <div className="modal-actions">
          <button type="button" className="ghost" onClick={onClose}>
            {dict.common.cancel}
          </button>
          <button type="submit" disabled={busy || !rateLimitValid}>
            {busy ? dict.keys.editSaving : dict.keys.editSave}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * The once-only secret reveal.
 *
 * Deliberately not dismissible by backdrop click or Escape — the customer
 * must tick the acknowledgement first. This is the only place in the portal
 * where a value this sensitive is ever rendered, and it is never retrievable
 * again once this closes.
 */
function SecretReveal({ apiKey, onDismiss }: { apiKey: CreatedKey; onDismiss: () => void }) {
  const { dict } = useLanguage();
  const [acknowledged, setAcknowledged] = useState(false);
  const { copied, copy } = useCopyToClipboard();

  return (
    <Modal title={dict.keys.secretModalTitle} dismissible={false}>
      <p className="warn-text">
        <strong>{dict.keys.secretWarning}</strong> {dict.keys.secretWarningBody}
      </p>

      <div className="secret-box">
        <code>{apiKey.secret}</code>
        <button type="button" className="ghost" onClick={() => void copy(apiKey.secret)}>
          {copied ? dict.keys.copied : dict.keys.copy}
        </button>
      </div>

      {gatewayOpenAiBaseUrl() && (
        <p className="base-url-hint">
          <InfoCircleFilled className="base-url-hint__icon" aria-hidden="true" />
          {dict.keys.secretBaseUrlHint(gatewayOpenAiBaseUrl()!)}
        </p>
      )}

      <label className="checkbox-row">
        <input type="checkbox" checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} />
        {dict.keys.ackLabel}
      </label>

      <div className="modal-actions">
        <button type="button" disabled={!acknowledged} onClick={onDismiss}>
          {dict.keys.done}
        </button>
      </div>
    </Modal>
  );
}
